import type { InitialConfig, ItemsResponse } from '@src/types';
import { resizeVtexImage, sortSizes } from '@core/utils';
import { metaAdapter } from '@meta/index';
import { normalizeBrand } from '@src/core/farm';
import { getItemCategory, getItemCategory2 } from './utils';
import type {
  DecoAvailableGridContext,
  DecoBrandContext,
  DecoItemsContext,
  DecoLineItemsContext,
  DecoValueContext,
  PropertyValue,
  Product,
} from "./types";


const IN_STOCK_SCHEMA = "https://schema.org/InStock";
const LIST_PRICE_SCHEMA = "https://schema.org/ListPrice"

export const decoAdapter = {
  ...metaAdapter, // global adapters

  items: (context: DecoItemsContext): ItemsResponse[] | null => {
    if (context?.items) return context.items

    if (context?.products) {
      return context.products.map((item: Product) => {
        const COLOR_REGEX = /^[^_]+_/ // do começo da string, captura tudo que não é underscore, até achar um underscore.
        const listPrice = item?.offers?.offers?.[0].priceSpecification.find((s) =>
          s.priceType === LIST_PRICE_SCHEMA
        )?.price

        return {
          quantity: 1,
          index: item?.index ?? 0,
          item_brand: item.brand?.name?.toLowerCase() ?? null,
          item_name: item.alternateName ?? null,
          price: item.offers?.lowPrice ?? null,
          discount: Math.round((Number(listPrice) - Number(item?.offers?.lowPrice)) * 100) / 100,
          item_variant: item?.name?.split(' - ')[0]?.replace(COLOR_REGEX, '').replace(/_/g, ' ') || null, // cor
          item_variant2: item?.name?.split(' - ')[1] ?? null, // tamanho
          item_shipping_tier: '',
          item_ref: item.additionalProperty?.find((i: PropertyValue) => i.name === "RefId")?.value?.replace(/_\d+$/, "") ?? null,
          item_category: getItemCategory(item) ?? null,
          item_category2: getItemCategory2(item) ?? null,
          item_id: item?.inProductGroupWithID ?? null,
          item_sku: item.sku ?? null,
          item_url: item.url ?? null,
          image_url: resizeVtexImage(item?.image?.[0].url) ?? null,
          seller_id: item.offers?.offers?.[0]?.seller ?? null,
          item_list_name: item?.item_list_name ?? null,
        }
      })
    }

    return null
  },

  brand: (context: DecoBrandContext, config: InitialConfig) => {
    if (config?.brand) return config.brand
    if (context?.brand) return context.brand
    if (context?.products) {
      return normalizeBrand(context.products.map((p) => p?.brand?.name))
    }
    return null
  },

  line_items: (context: DecoLineItemsContext): string | null => {
    if (context?.line_items) return context.line_items;

    if (context?.products) {
      return context.products
        .map((item: Product) => item?.inProductGroupWithID)
        .filter((id): id is string => Boolean(id))
        .join()
    }

    return null;
  },

  value: (context: DecoValueContext): number | null => {
    if (context?.value) return context.value

    if (context?.products) {
      return context.products
        .map((item: Product) => item.offers?.lowPrice)
        .filter((price): price is number => price !== undefined)
        .reduce((sum, price) => sum + price, 0)
    }

    return null
  },

  available_grid: (context: DecoAvailableGridContext): string | null => {
    if (context?.available_grid) return context.available_grid

    if (context?.products) {
      const sizes = context.products
        .flatMap((product: Product) =>
          product.isVariantOf?.hasVariant?.filter((variant) =>
            variant.offers?.offers?.some((o) => o.availability === IN_STOCK_SCHEMA)
          )
            .map((variant) => variant.name?.split(' - ')[1])
            .filter((name): name is string => Boolean(name)) ?? []
        )
      const result = sortSizes(sizes).join(",").trim()

      return result || null
    }

    return null;
  },
}